'use client';

import React, { memo } from 'react';
import { Image } from '@sitecore-jss/sitecore-jss-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Parallax, ParallaxProvider } from 'react-scroll-parallax';
import CardButtons from './CardButtons/CardButtons';
import styles from './ParallaxCard.module.scss';
import OptimizedImage from '../../../ui/OptimizedImage/OptimizedImage';
import { useMediaQuery } from 'usehooks-ts';

const ParallaxCard = memo(function ParallaxCard({ aboutBlock }) {
  const isMobile = useMediaQuery('(max-width: 480px)');
  const isTablet = useMediaQuery('(max-width: 834px)');

  // 安全检查：如果aboutBlock为空，返回空组件
  if (!aboutBlock) {
    return null;
  }

  const getImageUrl = (imageData) => {
    if (imageData && imageData.fields) {
      // 优先使用 desktopImage，然后是 tabletImage，最后是 mobileImage
      return (
        imageData.fields.desktopImage?.value?.src ||
        imageData.fields.tabletImage?.value?.src ||
        imageData.fields.mobileImage?.value?.src ||
        ''
      );
    }
    if (imageData && imageData.value) {
      return imageData.value.href || imageData.value.src || '';
    }
    if (imageData && imageData.url) {
      return imageData.url;
    }
    return imageData?.url || imageData?.src || '';
  };

  const getMobileImageUrl = (imageData) => {
    if (imageData && imageData.fields) {
      // 优先使用 mobileImage，然后是 tabletImage，最后是 desktopImage
      return (
        imageData.fields.mobileImage?.value?.src ||
        imageData.fields.tabletImage?.value?.src ||
        imageData.fields.desktopImage?.value?.src ||
        ''
      );
    }
    if (imageData && imageData.value) {
      return imageData.value.href || imageData.value.src || '';
    }
    if (imageData && imageData.url) {
      return imageData.url;
    }
    return imageData?.url || imageData?.src || '';
  };

  const getTitle = (data) => {
    if (data && data.value) {
      return data.value;
    }
    if (data && data.title) {
      return data.title.value || data.title;
    }
    return data?.title || '';
  };

  const getText = (data) => {
    if (data && data.value) {
      return data.value;
    }
    if (data && data.text) {
      return data.text.value || data.text;
    }
    return data?.text || '';
  };

  const imageUrl = getImageUrl(aboutBlock.image);
  // 使用aboutImage的mobileImage字段
  const mobileImageUrl = getMobileImageUrl(aboutBlock.image);
  const title = getTitle(aboutBlock.title);
  const text = getText(aboutBlock.text);

  if (!imageUrl) {
    return null;
  }

  // 根据设备类型获取图片配置
  const getImageConfig = () => {
    if (isMobile) {
      return {
        width: 480,
        height: 600,
        device: 'mobile',
        quality: 70,
      };
    } else if (isTablet) {
      return {
        width: 834,
        height: 800,
        device: 'tablet',
        quality: 75,
      };
    } else {
      return {
        width: 1200,
        height: 800,
        device: 'desktop',
        quality: 80,
      };
    }
  };

  const imageConfig = getImageConfig();

  return (
    <div className="container">
      <ParallaxProvider>
        <Parallax speed={15}>
          <div className={styles.wrapper} data-aos="fade-up">
            <div className={styles.content}>
              <OptimizedImage
                src={imageUrl}
                alt=""
                width={imageConfig.width}
                height={imageConfig.height}
                loading="lazy"
                quality={imageConfig.quality}
                device={imageConfig.device}
                objectFit="cover"
                className={`${styles.parallax__image} ${styles.desktopImage}`}
              />
              <OptimizedImage
                src={mobileImageUrl}
                alt=""
                width={480}
                height={600}
                loading="lazy"
                quality={70}
                device="mobile"
                objectFit="cover"
                className={`${styles.parallax__image} ${styles.mobileImage}`}
              />
              <div className={styles.parallax__item}>
                <div className={styles.parallax__container}>
                  <div className={styles.parallax__text}>
                    <h4 className={styles.parallax__title} data-aos="fade-up" data-aos-delay="300">
                      {title}
                    </h4>
                    <div
                      className={styles.parallax__description}
                      data-aos="fade-up"
                      data-aos-delay="300"
                      dangerouslySetInnerHTML={{ __html: text }}
                    />
                  </div>
                  <div className={styles.parallax__buttons} data-aos="fade-up" data-aos-delay="300">
                    <CardButtons
                      hasBorder
                      aboutButtonText={aboutBlock.aboutButtonText}
                      aboutButtonUrl={aboutBlock.aboutButtonUrl}
                      contactButtonText={aboutBlock.contactButtonText}
                      contactButtonUrl={aboutBlock.contactButtonUrl}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.parallax__buttons_xxl}>
              <CardButtons
                aboutButtonText={aboutBlock.aboutButtonText}
                aboutButtonUrl={aboutBlock.aboutButtonUrl}
                contactButtonText={aboutBlock.contactButtonText}
                contactButtonUrl={aboutBlock.contactButtonUrl}
              />
            </div>
          </div>
        </Parallax>
      </ParallaxProvider>
    </div>
  );
});

export default ParallaxCard;
